/* eslint-disable indent */
import { ChangeEvent, useEffect, useRef, useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { NotFound } from '@/components/Message/NotFound';
import { Button } from '@/components/Button';
import Loader from '@/components/Loading/Loader';
import { ResultVideo } from './ResultVideo';
import { TFunction } from 'i18next';
import { ResultPhotoTab } from '@/type/ui/tab/vehiculeCheckResultTab';
import { TVehiculeCheckResultSchema } from '@/schemas/vehicule-check-result/vehicule-check-result.schema';
import { useGetVehiculeCheckVideo } from '@/hooks/vehicule-check-result.hooks';
import { useValidateModeration } from '@/hooks/vehicule-check.hooks';
import { useGetChildTab } from '@/hooks/tab';
import {
  useUpdateVehiculeCheckResult,
  useGetValidateReport,
  useCustomizeResultImage,
} from '@/hooks/vehicule-check-result.hooks';
import { ResultAccordionBody } from './Accordion/ResultAccordionBody';
import { ResultField } from './ResultField';
import { TabItem } from '@/components/Tab/TabItem';
import { TabList } from '@/components/Tab/TabList';
import Card from '@/components/Card';
import { ResultFraud } from './ResultFraud';
import { ResultImmatVIN } from './ResultImmatVIN';
import { ResultPhotoList } from './ResultPhotoList';
import { Select } from '@/components/Controls/Select';
import { InfosMessage } from '@/components/Message/InfosMessage';
import { TextInfo } from '@/components/Message/TextInfo';
import { IconPencilCheck } from '@tabler/icons-react';
import { TEquipmentSchema } from '@/schemas/equipment/equipment.schema';
import { TDamageSchema } from '@/schemas/damage/damage.schema';
import { TVehiculeSchema } from '@/schemas/vehicule/vehicule.schema';
import { showModal } from '@/utils/modal.utils';
import {
  formatSecondsToMinutesSeconds,
  getDifferenceInHoursMinutesSeconds,
} from '@/utils/date.utils';
import { getModerateText } from '../utils/result-details.utils';
import { HumanModerationStatus } from '@/api/entities/moderation';
import {
  API_BASE_URL,
  getOptionsReportVersion,
  R2_PUBLIC_DOMAIN,
} from '@/utils/constant.utils';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { useResultImageList } from '../hooks/result-image.hooks';
import { Accordion } from '@/components/Accordion';
import { handleToggleAccordion } from '@/utils/accordeon.utils';
import { ImagePreviewModal } from '@/components/Custom/Modal/ImagePreviewModal';
import { CheckPointPhoto } from './CheckPointPhoto';
import { useAuth } from '@/hooks/auth.hooks';
import {
  IconAlertTriangle,
  IconAlertTriangleFilled,
} from '@tabler/icons-react';
import { useListAllCheckPoints } from '@/hooks/check-point.hooks';
import { useGetTranslatedCheckPoint } from '@/hooks/translated-check-point.hooks';
import { getTranslatedItem } from '@/utils/language.utils';
import { VehiculeCheckProgressIndicator } from '@/components/Custom/ProgressIndicator/VehiculeCheckProgressIndicator';
import { useVehiculeCheckVideoSockets } from '@/hooks/sockets/vehicule-check-video-sockets.hooks';
import { useQueryClient } from '@tanstack/react-query';
import { Switch } from '@/components/Controls/Switch';
import { useGetGroupById } from '@/hooks/group.hooks';
import { removedUrlQueriesFromPdfRedirect } from '../utils/result-image.utils';
// import { useGetTotalCostingByVehiculeCheckId } from '@/hooks/repair-costing.hooks';

interface VehiculeCheckDetailedResultsProps {
  t: TFunction<undefined>;
  isLoading?: boolean;
  vehiculeCheckResult?: TVehiculeCheckResultSchema;
  groupId?: string;
  isReadOnly?: boolean;
  isAdmin?: boolean;
  isModerator?: boolean;
  isModerationMandatory: boolean;
  moderationStatus?: string;
  reportValidated?: boolean;
}

export const VehiculeCheckDetailedResults = ({
  t,
  isLoading,
  vehiculeCheckResult,
  groupId,
  isReadOnly,
  // isAdmin,
  isModerator,
  isModerationMandatory,
  moderationStatus,
}: VehiculeCheckDetailedResultsProps) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const urlCheckPointId = searchParams.get('checkPointId');
  const urlResultObjectId = searchParams.get('resultObjectId');
  const urlToDamageList = searchParams.get('toDamageList');
  const urlCheckPointImage = searchParams.get('checkPointImageUrl');

  const { loggedInUser } = useAuth();
  const queryClient = useQueryClient();
  const [selectedCheckPointId, setSelectedCheckPointId] = useState<
    string | null
  >(null);

  const [resultsDetail, setResultsDetail] = useState(null);
  const [versionDisplayed, setVersionToDisplayed] = useState<string>('2');

  // const reportType = useMemo(() => {
  //   if (versionDisplayed === '1') {
  //     return 'moderator';
  //   }
  //   if (versionDisplayed === '2') {
  //     return 'client';
  //   }
  //   return 'initial';
  // }, [versionDisplayed]);

  const [fraudConfirmed, setFraudConfirmed] = useState<boolean>(false);
  const [hasInitializedAccordion, setHasInitializedAccordion] =
    useState<boolean>(false);
  const [checkpointImgToPreview, setCheckpointImgToPreview] = useState('');
  const preloadedImagesRef = useRef<Set<string>>(new Set());
  const [checkPointIdToCustomize, setCheckPointIdToCustomize] = useState<
    string[]
  >([]);
  const [customizedCheckPointImages, setCustomizedCheckPointImages] = useState<
    string[]
  >([]);

  const resultContainsEquipments = useMemo(() => {
    return vehiculeCheckResult?.results?.some(
      (item) => item?.checkPoint?.equipments?.length > 0,
    );
  }, [vehiculeCheckResult]);

  const resultContainsDamages = useMemo(() => {
    return vehiculeCheckResult?.results?.some(
      (item) => item?.checkPoint?.damages?.length > 0,
    );
  }, [vehiculeCheckResult]);

  const {
    uncachedVideoUrl,
    videoUrl,
    isLoading: isLoadingVideo,
  } = useGetVehiculeCheckVideo(id);
  const { mutate: updateVehiculeCheckResult } = useUpdateVehiculeCheckResult();
  const { mutate: validateModeration, isPending: isValidationLoading } =
    useValidateModeration({ t });
  const { mutate: validateReport } = useGetValidateReport({ t });
  const { currentTab, navigateToTab } = useGetChildTab({
    defaultTab: ResultPhotoTab.EQUIPMENTS,
  });
  const vehiculeCheckResultExists = !!vehiculeCheckResult;
  const { data: allCheckPoints, isLoading: isLoadingAllCheckPoints } =
    useListAllCheckPoints();
  const { getTranslatedCheckPoint } = useGetTranslatedCheckPoint();

  // Display chapters data ResultVideo
  const videoChapterData = useMemo(() => {
    if (!vehiculeCheckResult?.results?.length) return [];

    // const videoCheckPoints = vehiculeCheckResult.results.filter(
    //   (item) => item?.checkPoint?.isVideo,
    // );

    const videoCheckPoints = vehiculeCheckResult.results;

    return videoCheckPoints.map((item, index) => {
      const translatedCheckPoint = getTranslatedCheckPoint(
        item?.checkPoint?.id,
      );
      const startTime = item?.checkPoint?.startTimeInVideo || 0;
      const nextItem = videoCheckPoints[index + 1];
      // const endTime = nextItem?.checkPoint?.startTimeInVideo || startTime + 10;
      const endTime = nextItem?.checkPoint?.startTimeInVideo ?? Infinity;
      return {
        startTime,
        endTime,
        // title: translatedCheckPoint?.name || item?.checkPoint?.name,
        title: `${translatedCheckPoint?.name || item?.checkPoint?.name} ${item?.checkPoint?.isVideo ? '' : `(${t('common:photo')})`}`,
      };
    });
  }, [vehiculeCheckResult, getTranslatedCheckPoint, t]);

  const { data: group, isLoading: isLoadingGroup } = useGetGroupById(groupId);

  // const { data: totalCosting, isLoading: isLoadingTotalCosting } =
  //   useGetTotalCostingByVehiculeCheckId({
  //     vehiculeCheckId: id,
  //     reportType,
  //   });

  const groupCustomImage = group?.data?.groupCustomImage;

  const groupHasCustomImageData =
    groupCustomImage &&
    Object.values(groupCustomImage).some(
      (value) => value !== undefined && value !== null,
    );

  const handleCloseCheckpointImagePreviewModal = () => {
    removedUrlQueriesFromPdfRedirect();
  };

  const {
    mutate: customizeResultImage,
    isPending: isPendingCustomizeResultImage,
  } = useCustomizeResultImage({
    handleSuccess: (data) => {
      const customizedImageUrl = `${R2_PUBLIC_DOMAIN}/${data?.['data']?.customizedImage}`;

      if (!customizedCheckPointImages.includes(customizedImageUrl)) {
        setCustomizedCheckPointImages([
          ...customizedCheckPointImages,
          customizedImageUrl,
        ]);
      }
    },
  });

  // const { mutate: createVehiculeCheckResult } = useCreateVehiculeCheckResult()

  /**
   * === Redirect to Detailed Results if NOT logged in and NOT within allowed child tab ===
   */
  useEffect(() => {
    if (
      !loggedInUser &&
      currentTab !== ResultPhotoTab.EQUIPMENTS &&
      currentTab !== ResultPhotoTab.DAMAGES
    ) {
      navigateToTab(ResultPhotoTab.EQUIPMENTS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUser, currentTab]);
  /**
   * === END OF Redirect to Detailed Results if NOT logged in and NOT within allowed child tab ===
   */

  const damagesCheckPointId = resultsDetail?.results?.find(
    (result) => result?.checkPoint?.damages,
  )?.checkPoint?.id;

  const damageList = useResultImageList(resultsDetail?.results, 'damages');

  const editAllowed = useMemo(() => {
    if (versionDisplayed === '0') {
      return false;
    }
    if (isModerator && moderationStatus === HumanModerationStatus.A_MODERER) {
      return true;
    }
    if (versionDisplayed === '2' && !vehiculeCheckResult?.reportValidated)
      return true;
    return false;
  }, [versionDisplayed, isModerator, moderationStatus, vehiculeCheckResult]);

  const handleUpdateFraud = (fraudValue: boolean) => {
    const dataFraud = {
      ...vehiculeCheckResult,
      fraudConfirmed: fraudValue,
    };
    updateVehiculeCheckResult({ id: id, data: dataFraud });
  };

  const handleUpdateReportView = (e: ChangeEvent<HTMLSelectElement>) => {
    setVersionToDisplayed(e.target.value);
  };

  const displayValidatenModerationBtn = useMemo(
    () =>
      isModerator &&
      versionDisplayed === '1' &&
      moderationStatus === HumanModerationStatus.A_MODERER,
    [isModerator, versionDisplayed, moderationStatus],
  );

  const moderationMandatoryAndValidated = useMemo(() => {
    if (
      isModerationMandatory &&
      moderationStatus === HumanModerationStatus.MODERE
    ) {
      return true;
    }
    if (!isModerationMandatory) {
      return true;
    }
    return false;
  }, [isModerationMandatory, moderationStatus]);

  const displayValidateReport = useMemo(
    () =>
      versionDisplayed === '2' &&
      !vehiculeCheckResult?.reportValidated &&
      moderationMandatoryAndValidated,
    [versionDisplayed, vehiculeCheckResult, moderationMandatoryAndValidated],
  );

  /**
   * === Hook for Vehicule Check Video Sockets ===
   */
  const {
    vehiculeCheckProgressStatus,
    videoUploadProgressPercent,
    isVideoProgressCompleted,
  } = useVehiculeCheckVideoSockets({
    vehiculeCheckId: id,
  });
  /**
   * === END OF Hook for Vehicule Check Video Sockets ===
   */

  // Ensure videoUrl is re-checked once processing completes (avoid needing a page refresh)
  useEffect(() => {
    if (isVideoProgressCompleted && id) {
      queryClient.invalidateQueries({
        queryKey: ['vehiculeCheckVideo', uncachedVideoUrl],
      });
    }
  }, [isVideoProgressCompleted, id, uncachedVideoUrl]);

  useEffect(() => {
    if (versionDisplayed === '1') {
      setResultsDetail(vehiculeCheckResult?.moderatorReport);
    } else if (versionDisplayed === '2') {
      setResultsDetail(vehiculeCheckResult?.clientReport);
    } else {
      setResultsDetail(vehiculeCheckResult?.initReport);
    }
  }, [versionDisplayed, vehiculeCheckResult]);

  /**
   * === Handle Moderation Status and Report Data ===
   */
  useEffect(() => {
    if (!isLoading && vehiculeCheckResult?.results?.length > 0) {
      if (
        moderationStatus === HumanModerationStatus.A_MODERER &&
        (isModerator || !loggedInUser)
      ) {
        setResultsDetail(vehiculeCheckResult?.moderatorReport);
        setVersionToDisplayed('1');
      }

      if (
        moderationStatus === HumanModerationStatus.MODERE &&
        (!isModerator || !loggedInUser)
      ) {
        setResultsDetail(vehiculeCheckResult?.clientReport);
        setVersionToDisplayed('2');
      }

      setFraudConfirmed(vehiculeCheckResult?.fraudConfirmed);
    }
  }, [
    isLoading,
    vehiculeCheckResult,
    moderationStatus,
    isModerator,
    loggedInUser,
  ]);

  /**
   * === Preload Checkpoint Images ===
   */
  useEffect(() => {
    if (!isLoading && resultsDetail?.results?.length > 0) {
      // Preload all checkpoint images to improve accordion opening performance
      resultsDetail.results.forEach((item) => {
        if (item?.checkPoint?.imageCheckPoint && !item?.checkPoint?.isVideo) {
          const imageUrl = `${R2_PUBLIC_DOMAIN}/${item.checkPoint.imageCheckPoint}`;

          // Skip if already preloaded
          if (preloadedImagesRef.current.has(imageUrl)) {
            return;
          }

          const img = new Image();
          img.onload = () => {
            // Mark as preloaded
            preloadedImagesRef.current.add(imageUrl);
          };
          img.onerror = () => {
            // Still mark as attempted to avoid retrying
            preloadedImagesRef.current.add(imageUrl);
          };
          img.src = imageUrl;
        }
      });
    }
  }, [isLoading, resultsDetail]);
  /**
   * === END OF Preload Checkpoint Images ===
   */

  /**
   * === Open First Check Point Accordion on Initial Load ===
   */
  useEffect(() => {
    if (
      !isLoading &&
      resultsDetail?.results?.length > 0 &&
      !hasInitializedAccordion
    ) {
      // Get first checkpoint ID from the already filtered resultsDetail
      const firstCheckPointId =
        urlCheckPointId || resultsDetail?.results['0']?.checkPoint?.id;

      if (firstCheckPointId) {
        setSelectedCheckPointId(firstCheckPointId);
      }

      // Scroll to the checkpoint if urlCheckPointId is present
      if (urlCheckPointId) {
        // Wait for React to render the accordion body after setSelectedCheckPointId
        setTimeout(() => {
          const element = document.getElementById(urlCheckPointId);
          if (element) {
            const headerOffset = loggedInUser ? 160 : 90;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition =
              elementPosition + window.scrollY - headerOffset;
            window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
          }

          // Open checkpoint image preview after scroll completes
          if (urlCheckPointImage) {
            const imageUrl = `${R2_PUBLIC_DOMAIN}/${urlCheckPointImage}`;
            setTimeout(() => {
              setCheckpointImgToPreview(imageUrl);
              showModal('#checkpoint-image-preview-modal');
            }, 200);
          }
        }, 500);
      }

      // Scroll to the damage list section if urlToDamageList is true
      if (urlToDamageList === 'true') {
        setTimeout(() => {
          const element = document.getElementById('to-damage-list-container');
          if (element) {
            const headerOffset = loggedInUser ? 80 : 12;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition =
              elementPosition + window.scrollY - headerOffset;
            window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
          }
        }, 500);
      }

      setHasInitializedAccordion(true);
    }
  }, [
    isLoading,
    resultsDetail,
    hasInitializedAccordion,
    urlCheckPointId,
    loggedInUser,
    urlCheckPointImage,
    urlToDamageList,
  ]);
  /**
   * === END OF Accordion Initialization ===
   */

  const onToggleAccordion = (id: string) => {
    handleToggleAccordion({
      id,
      headerOffset: loggedInUser ? 80 : 10,
      selectedId: selectedCheckPointId,
      setSelectedId: setSelectedCheckPointId,
    });
    // Remove preview-related query params from the URL
    removedUrlQueriesFromPdfRedirect();
  };

  const onPreviewCheckpointImage = (imageUrl: string) => {
    setCheckpointImgToPreview(imageUrl);
    showModal('#checkpoint-image-preview-modal');
  };

  const renderResultAccordions = () => {
    if (!isLoading && resultsDetail?.results?.length > 0) {
      return resultsDetail?.results?.map((item) => {
        const isVideoCheckPoint = item?.checkPoint?.isVideo;

        const viewCustomCheckPointImage = checkPointIdToCustomize?.includes(
          item?.checkPoint?.id,
        );

        const checkPointCustomImageUrl = `${R2_PUBLIC_DOMAIN}/images/group/${groupId}/custom_${item?.checkPoint?.imageCheckPoint
          ?.split('/')
          ?.pop()}`;

        const customizedCheckPointImageExists =
          customizedCheckPointImages.includes(checkPointCustomImageUrl);

        const checkPointImageUrl =
          viewCustomCheckPointImage && customizedCheckPointImageExists
            ? checkPointCustomImageUrl
            : `${R2_PUBLIC_DOMAIN}/${item?.checkPoint?.imageCheckPoint}`;

        const validatedManually = item?.checkPoint?.validatedManually;

        const skipped = item?.checkPoint?.skipped;

        // Get check point
        const checkPoint = allCheckPoints?.data?.find(
          (checkPoint) => checkPoint?.id === item?.checkPoint?.id,
        );

        // Get translated check point
        const translatedCheckPoint = getTranslatedItem(
          checkPoint?.translatedCheckPoints,
        );

        const isLicensePlatePhotoCheckPoint =
          !isVideoCheckPoint && checkPoint?.enableLicensePlateDetection;

        const isVINPhotoCheckPoint =
          !isVideoCheckPoint && checkPoint?.enableVINDetection;

        const isVehicleRegistrationLicenseCheckPoint =
          !isVideoCheckPoint && checkPoint?.enableRegistrationCertificate;

        const isTraditionalPhotoCheckPoint = !(
          isLicensePlatePhotoCheckPoint ||
          isVINPhotoCheckPoint ||
          isVehicleRegistrationLicenseCheckPoint
        );

        const equipmentImagesHavePolygon = item?.checkPoint?.equipments?.some(
          (item) =>
            item?.images?.some(
              (image) => image?.coordinatesModerated?.point?.length > 0,
            ),
        );

        return (
          <div
            key={item?.checkPoint?.id}
            data-accordion-id={item?.checkPoint?.id}
            className={twMerge(
              '',
              isPendingCustomizeResultImage && 'opacity-70 pointer-events-none',
            )}
          >
            <Accordion
              titleClassName="uppercase flex flex-1"
              bodyClassName="m-0"
              active={selectedCheckPointId === item?.checkPoint?.id}
              title={
                <div className="-mt-0.5 flex flex-1 gap-1.5 items-center max-sm:justify-between">
                  <div className="flex flex-1 items-center gap-2.5">
                    {!isLoadingAllCheckPoints && (
                      <div className="w-6.5 min-w-6.5 h-6.5 min-h-6.5 flex items-center justify-center max-sm:hidden">
                        <img
                          src={`${API_BASE_URL}${checkPoint?.reportIcon}`}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    )}
                    <div className="mt-0.75">
                      {translatedCheckPoint?.name || item?.checkPoint?.name}
                    </div>
                  </div>

                  {isVideoCheckPoint &&
                    (skipped === true ? (
                      <div className="flex items-center gap-2 max-sm:items-start max-sm:gap-[4px] max-sm:max-w-[90px] max-sm:justify-end">
                        <IconAlertTriangle className="w-6 min-w-6 h-6 min-h-6 text-yellow-500 max-sm:w-3.5 max-sm:min-w-3.5 max-sm:h-3.5 max-sm:min-h-3.5" />
                        <div className="text-2sm font-medium tracking-wide [word-spacing:0.075em] max-sm:py-0 max-sm:text-[9.5px] max-sm:leading-tight">
                          {t('skippedByUser')}
                        </div>
                      </div>
                    ) : validatedManually === true ? (
                      <div className="border border-yellow-500 text-yellow-500 pt-1.5 pb-1 px-3 rounded-md text-2sm font-medium tracking-wide [word-spacing:0.075em] max-sm:h-5 max-sm:rounded-[3px] max-sm:py-0 max-sm:px-1 max-sm:text-[8px]">
                        {t('notValidatedByAI')}
                      </div>
                    ) : validatedManually === false ? (
                      <div className="border border-green-400 text-green-400 pt-1.5 pb-1 px-3 rounded-md text-2sm font-medium tracking-wide [word-spacing:0.075em] max-sm:h-5 max-sm:rounded-[3px] max-sm:py-0 max-sm:px-1 max-sm:text-[8px]">
                        {t('validatedByAI')}
                      </div>
                    ) : (
                      <></>
                    ))}
                </div>
              }
              onClick={() => onToggleAccordion(item?.checkPoint?.id)}
            >
              <ResultAccordionBody>
                <div
                  id={item?.checkPoint?.id}
                  className="flex flex-wrap justify-between gap-10 max-sm:gap-5"
                >
                  <div className="flex flex-col gap-1 flex-1 max-2xl:flex-none max-2xl:w-full">
                    {isTraditionalPhotoCheckPoint && (
                      <>
                        {skipped ? (
                          <div className="flex gap-2.5 bg-purple-EDECFB/60 px-2.75 pt-4 pb-3 rounded">
                            <IconAlertTriangleFilled
                              // className="-mt-0.5 text-yellow-FF9500"
                              className="text-yellow-FF9500"
                              size={27}
                            />
                            {/* <div className="text-orange-FC450E/90 font-semibold"> */}
                            <div className="mt-0.75 text-orange-FC450E/90 font-semibold max-sm:text-2sm max-sm:leading-tight">
                              {t('userSkippedThisCheckpoint')}
                            </div>
                          </div>
                        ) : (
                          <>
                            {isVideoCheckPoint &&
                              validatedManually === true && (
                                <div className="flex gap-2.5 bg-purple-EDECFB/60 px-2.75 pt-4 pb-3 rounded">
                                  <div>
                                    <i className="ki-filled ki-information-2 text-orange-FC450E/90 text-[21px]" />
                                  </div>
                                  <div className="text-orange-FC450E/90 font-semibold max-sm:text-2sm max-sm:leading-tight">
                                    {t('userValidatedThisCheckpointManually')}
                                  </div>
                                </div>
                              )}
                            <ResultField
                              text={`${t('common:nbElementsDetected')} :`}
                            >
                              <p className="font-semibold">
                                {item?.checkPoint?.equipments?.length || 0}
                              </p>
                            </ResultField>
                            <ResultField
                              text={`${t('common:nbDamagesDetected')} :`}
                            >
                              <p className="font-semibold">
                                {item?.checkPoint?.damages?.length || 0}
                              </p>
                            </ResultField>
                          </>
                        )}
                        <ResultField
                          containerClassName="mt-5 p-0 bg-transparent"
                          textClassName="text-md text-black font-bold"
                          text={`${t('common:moderation')} :`}
                        />
                        <ResultField
                          text={`${t('common:nbCorrectionsOfElements')} :`}
                        >
                          <p className="font-semibold">
                            {t('common:notDoneYet')}
                          </p>
                        </ResultField>
                        <ResultField
                          text={`${t('common:nbCorrectionsOfDamages')} :`}
                        >
                          <p className="font-semibold">
                            {t('common:notDoneYet')}
                          </p>
                        </ResultField>
                        {isVideoCheckPoint && (
                          <>
                            <ResultField
                              containerClassName="mt-5"
                              text={`${t('common:videoDuration')} :`}
                            >
                              <p className="font-semibold">
                                {getDifferenceInHoursMinutesSeconds(
                                  item?.checkPoint?.startVideo,
                                  item?.checkPoint?.endVideo,
                                )}
                              </p>
                            </ResultField>
                            <ResultField
                              containerClassName="mt-5"
                              text={`${t('startTimeInVideo')} :`}
                            >
                              <p className="font-semibold">
                                {item?.checkPoint?.startTimeInVideo
                                  ? formatSecondsToMinutesSeconds(
                                      item?.checkPoint?.startTimeInVideo,
                                    )
                                  : 0}
                              </p>
                            </ResultField>
                            <ResultField text={`${t('endTimeInVideo')} :`}>
                              <p className="font-semibold">
                                {item?.checkPoint?.endTimeInVideo
                                  ? formatSecondsToMinutesSeconds(
                                      item?.checkPoint?.endTimeInVideo,
                                    )
                                  : 0}
                              </p>
                            </ResultField>
                          </>
                        )}
                      </>
                    )}
                    {isLicensePlatePhotoCheckPoint && (
                      <ResultField text={`${t('common:licensePlate')} :`}>
                        <p className="font-semibold">
                          {vehiculeCheckResult?.valueImmatriculation ||
                            t('notMentionedDuringInspection')}
                        </p>
                      </ResultField>
                    )}
                    {isVINPhotoCheckPoint && (
                      <ResultField text={`${t('common:VIN')} :`}>
                        <p className="font-semibold">
                          {vehiculeCheckResult?.valueVin ||
                            t('notMentionedDuringInspection')}
                        </p>
                      </ResultField>
                    )}
                    {isVehicleRegistrationLicenseCheckPoint && (
                      // TODO: Add vehicle registration certificate when its informations will be available
                      // TODO: Informations should be A1, A2, A3, P3, SE, V9, etc.
                      <ResultField>
                        <p className="font-semibold">
                          {t('common:infosNotAvailable')}
                        </p>
                      </ResultField>
                    )}
                  </div>
                  <div className="flex flex-col items-center flex-1 max-2xl:flex-none max-2xl:w-full">
                    {isVideoCheckPoint ? (
                      <>
                        <div className="max-sm:w-full">
                          {selectedCheckPointId === item?.checkPoint?.id ? (
                            <ResultVideo
                              t={t}
                              key={`${item?.checkPoint?.id}-${item?.checkPoint?.startTimeInVideo}`}
                              url={videoUrl}
                              isLoadingVideo={isLoadingVideo}
                              startTime={item?.checkPoint?.startTimeInVideo}
                              endTime={item?.checkPoint?.endTimeInVideo}
                              chapterData={videoChapterData}
                              detectionFiles={{
                                detectionsUrl: resultContainsEquipments
                                  ? `${R2_PUBLIC_DOMAIN}/videos/${id}-detection.txt`
                                  : undefined,
                                damagesUrl: resultContainsDamages
                                  ? `${R2_PUBLIC_DOMAIN}/videos/${id}-damage.txt`
                                  : undefined,
                              }}
                              showDetectionSwitchesAboveVideo={false}
                              // showEquipmentDetectionSwitch={
                              //   item?.checkPoint?.equipments?.length > 0
                              // }
                              // showDamageDetectionSwitch={
                              //   item?.checkPoint?.damages?.length > 0
                              // }
                              showEquipmentDetectionSwitch={
                                resultContainsEquipments
                              }
                              showDamageDetectionSwitch={resultContainsDamages}
                              containerClassName="max-sm:w-full"
                              videoClassName="h-[335px] bg-black max-sm:w-full"
                              detectionSwitchesContainerClassName="bg-gray-F6F9FB"
                              // detectionVideoContainerClassName="rounded-none"
                            />
                          ) : (
                            <div className="h-[335px]" />
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <CheckPointPhoto
                          t={t}
                          title={`${t('common:photo')} "${
                            item?.checkPoint?.name
                          }"`}
                          imageUrl={checkPointImageUrl}
                          onClick={() => {
                            onPreviewCheckpointImage(checkPointImageUrl);
                          }}
                        />
                        {!isReadOnly && groupHasCustomImageData && (
                          <div className="mt-4 flex justify-center">
                            <Switch
                              disabled={isPendingCustomizeResultImage}
                              checked={
                                checkPointIdToCustomize.find(
                                  (checkPointId) =>
                                    checkPointId === item?.checkPoint?.id,
                                )
                                  ? true
                                  : false
                              }
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setCheckPointIdToCustomize([
                                    ...checkPointIdToCustomize,
                                    item?.checkPoint?.id,
                                  ]);

                                  if (!customizedCheckPointImageExists) {
                                    customizeResultImage({
                                      groupId,
                                      imageToCustomize:
                                        item?.checkPoint?.imageCheckPoint,
                                    });
                                  }
                                } else {
                                  setCheckPointIdToCustomize(
                                    checkPointIdToCustomize.filter(
                                      (checkPointId) =>
                                        checkPointId !== item?.checkPoint?.id,
                                    ),
                                  );
                                }
                              }}
                              rightTextClassName="text-slate-700 font-medium"
                              rightText={t('viewCustomizedPhoto')}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <TabList className="mt-10 mb-5 gap-3.5">
                  <TabItem
                    className={twMerge(
                      '',
                      selectedCheckPointId === item?.checkPoint?.id &&
                        currentTab !== ResultPhotoTab.EQUIPMENTS &&
                        'border-black/50',
                    )}
                    text={t('common:equipmentPhotos')}
                    leftIcon={<i className="ki-outline ki-abstract-41" />}
                    active={currentTab === ResultPhotoTab.EQUIPMENTS}
                    onChange={() => navigateToTab(ResultPhotoTab.EQUIPMENTS)}
                  />
                  <TabItem
                    className={twMerge(
                      '',
                      selectedCheckPointId === item?.checkPoint?.id &&
                        currentTab !== ResultPhotoTab.DAMAGES &&
                        'border-black/50',
                    )}
                    text={t('common:damagePhotos')}
                    leftIcon={<i className="ki-outline ki-bucket-square" />}
                    active={currentTab === ResultPhotoTab.DAMAGES}
                    onChange={() => navigateToTab(ResultPhotoTab.DAMAGES)}
                  />
                  {!isReadOnly && !isModerator && (
                    <TabItem
                      className={twMerge(
                        '',
                        selectedCheckPointId === item?.checkPoint?.id &&
                          currentTab !== ResultPhotoTab.VEHICULES &&
                          'border-black/50',
                      )}
                      text={t('common:vehiculePhotos')}
                      leftIcon={<i className="ki-outline ki-parcel" />}
                      active={currentTab === ResultPhotoTab.VEHICULES}
                      onChange={() => navigateToTab(ResultPhotoTab.VEHICULES)}
                    />
                  )}
                </TabList>
                {selectedCheckPointId === item?.checkPoint?.id &&
                  currentTab === ResultPhotoTab.EQUIPMENTS && (
                    <ResultPhotoList<TEquipmentSchema>
                      t={t}
                      activeTab={currentTab}
                      checkPointId={item?.checkPoint?.id}
                      vehiculeCheckResult={vehiculeCheckResult}
                      data={item?.checkPoint?.equipments}
                      title={t('common:equipments')}
                      notFoundText={`${t('equipmentPhotosNotFound')}...`}
                      activateEngineCorrection={
                        isModerator && equipmentImagesHavePolygon
                      }
                      isModerator={isModerator}
                      moderationStatus={moderationStatus}
                      hideAddImage={isReadOnly}
                      hidePhotoActionButtons={isReadOnly || !editAllowed}
                      editAllow={editAllowed}
                      urlResultObjectId={urlResultObjectId}
                    />
                  )}
                {selectedCheckPointId === item?.checkPoint?.id &&
                  currentTab === ResultPhotoTab.DAMAGES && (
                    <ResultPhotoList<TDamageSchema>
                      t={t}
                      activeTab={currentTab}
                      checkPointId={item?.checkPoint?.id}
                      // checkPointDirection=""
                      vehiculeCheckResult={vehiculeCheckResult}
                      data={item?.checkPoint?.damages}
                      title={t('common:damages')}
                      notFoundText={`${t('damagePhotosNotFound')}...`}
                      activateEngineCorrection={isModerator}
                      isModerator={isModerator}
                      moderationStatus={moderationStatus}
                      hideAddImage={isReadOnly}
                      hidePhotoActionButtons={isReadOnly || !editAllowed}
                      editAllow={editAllowed}
                      urlResultObjectId={urlResultObjectId}
                    />
                  )}
                {!isReadOnly &&
                  selectedCheckPointId === item?.checkPoint?.id &&
                  currentTab === ResultPhotoTab.VEHICULES && (
                    <ResultPhotoList<TVehiculeSchema>
                      t={t}
                      activeTab={currentTab}
                      checkPointId={item?.checkPoint?.id}
                      vehiculeCheckResult={vehiculeCheckResult}
                      data={item?.checkPoint?.vehicules}
                      title={t('common:vehicules')}
                      notFoundText={`${t('vehiculePhotosNotFound')}...`}
                      moderationStatus={moderationStatus}
                      hidePhotoActionButtons={!editAllowed}
                      editAllow={editAllowed}
                      urlResultObjectId={urlResultObjectId}
                    />
                  )}
              </ResultAccordionBody>
            </Accordion>
          </div>
        );
      });
    }
  };

  return (
    <>
      <Card
        title={
          <TitleSection
            text={t('common:results')}
            defaultIconClassName="ki-notepad"
          />
        }
        bodyClassName="py-8 flex flex-col gap-5"
      >
        <>
          {isLoading ? (
            <></>
          ) : !vehiculeCheckResultExists ? (
            <div className="mt-7 mb-6">
              <NotFound text={`${t('resultsNotFound')}...`} />
            </div>
          ) : !isVideoProgressCompleted ? (
            <VehiculeCheckProgressIndicator
              t={t}
              progress={videoUploadProgressPercent}
              vehiculeCheckProgressStatus={vehiculeCheckProgressStatus}
            />
          ) : (
            <div>
              {!isReadOnly && (
                <div className="flex flex-col gap-5 max-md:gap-10 max-sm:gap-5">
                  <div className="flex flex-wrap gap-3 max-sm:gap-1">
                    <label className="font-semibold self-center">
                      {t('versionDisplayed')}
                      <span className="hidden max-sm:ml-1 max-sm:inline">
                        :
                      </span>
                    </label>
                    <Select
                      containerClassName="max-sm:flex-none max-sm:w-full"
                      selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500 max-w-60 max-sm:max-w-none"
                      options={getOptionsReportVersion(t, versionDisplayed)}
                      onChange={handleUpdateReportView}
                      value={versionDisplayed}
                    />
                  </div>
                  <InfosMessage containerClassName="w-full flex flex-col items-start gap-1.5 bg-purple-9D8AFF/[6%] rounded-lg pt-[18px] pb-[18.5px] px-6 max-md:gap-5">
                    {!vehiculeCheckResult?.reportValidated && (
                      <TextInfo
                        textClassName="font-medium"
                        text={getModerateText(
                          t,
                          isModerationMandatory,
                          moderationStatus,
                        )}
                      />
                    )}

                    {versionDisplayed !== '0' &&
                      moderationStatus === HumanModerationStatus.A_MODERER && (
                        <TextInfo
                          textClassName="font-medium"
                          text={t('inspection:moderationReportNB')}
                        />
                      )}

                    {vehiculeCheckResult?.reportValidated && (
                      <TextInfo
                        textClassName="font-medium"
                        text={t('inspection:reportHasBeenValidated')}
                      />
                    )}
                  </InfosMessage>
                  <ResultImmatVIN
                    immat={vehiculeCheckResult?.valueImmatriculation}
                    vin={vehiculeCheckResult?.valueVin}
                  />
                  {/* {(isAdmin || isModerator) && (
                    <div className="card bg-purple-600">
                      <div className="card-body flex justify-center items-center gap-2">
                        <div className="mt-[1.5px]">
                          <i className="ki-outline ki-euro text-[25px] text-white" />
                        </div>
                        <div className="flex justify-center text-md">
                          {isLoadingTotalCosting ? (
                            <div className="text-white text-md">{`${t('common:loading')}...`}</div>
                          ) : (
                            <>
                              <span className="text-white">
                                {t('totalRepairsCost')} :
                              </span>
                              <span className="ml-1 text-white font-medium">
                                {totalCosting?.data?.totalCost} €
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )} */}
                  {displayValidatenModerationBtn && (
                    <Button
                      buttonClassName="w-fit m-auto p-6"
                      leftIcon={<IconPencilCheck stroke={2} />}
                      text={
                        isValidationLoading
                          ? '...Chargement...'
                          : 'Valider la modération'
                      }
                      onClick={() => validateModeration(id)}
                    />
                  )}
                  {displayValidateReport && (
                    <Button
                      buttonClassName="w-fit m-auto p-6"
                      leftIcon={<IconPencilCheck stroke={2} />}
                      text={
                        isValidationLoading
                          ? '...Chargement...'
                          : 'Valider le rapport'
                      }
                      onClick={() => validateReport(vehiculeCheckResult?.id)}
                    />
                  )}
                  {vehiculeCheckResult?.fraudSuspicion && (
                    <ResultFraud
                      fraudConfirmed={fraudConfirmed}
                      setFraudConfirmed={() => {
                        setFraudConfirmed((prev) => !prev);
                        handleUpdateFraud(!fraudConfirmed);
                      }}
                      t={t}
                    />
                  )}
                </div>
              )}
              <div className="mt-5 space-y-5 max-sm:mt-9">
                {renderResultAccordions()}
              </div>
            </div>
          )}
        </>
      </Card>
      {isLoading ? (
        <></>
      ) : (
        isVideoProgressCompleted && (
          <div id="to-damage-list-container">
            <Card
              cardClassName="mt-4"
              title={
                <TitleSection
                  text={t('common:damages')}
                  defaultIconClassName="ki-bucket"
                />
              }
              bodyClassName="py-2 flex flex-col gap-5"
            >
              <ResultPhotoList<TDamageSchema>
                t={t}
                activeTab={ResultPhotoTab.DAMAGES}
                hidePhotoActionButtons
                checkPointId={damagesCheckPointId}
                vehiculeCheckResult={resultsDetail}
                hideAddImage
                data={damageList as TDamageSchema[]}
                title=""
                notFoundText={
                  damagesCheckPointId
                    ? `${t('damagePhotosNotFound')}...`
                    : `${t('damageCheckPointNotFound')}...`
                }
                moderationStatus={moderationStatus}
                imagePreviewModalId="preview-damage-repair-modal"
                urlResultObjectId={urlResultObjectId}
              />
            </Card>
          </div>
        )
      )}
      <ImagePreviewModal
        id="checkpoint-image-preview-modal"
        title={t('common:damages')}
        onClose={handleCloseCheckpointImagePreviewModal}
        onClickOutsideContent={handleCloseCheckpointImagePreviewModal}
        onEscapePress={handleCloseCheckpointImagePreviewModal}
      >
        <div>
          <img
            className="w-auto h-full object-contain object-center"
            src={checkpointImgToPreview}
            alt=""
          />
        </div>
      </ImagePreviewModal>
      {/* {(isLoading ||
        isLoadingVideo ||
        isLoadingGroup ||
        isPendingCustomizeResultImage ||
        isLoadingTotalCosting) && <Loader />} */}
      {(isLoading ||
        isLoadingVideo ||
        isLoadingGroup ||
        isPendingCustomizeResultImage) && <Loader />}
    </>
  );
};
